/**
 * LatestRitualsSection Component
 * 2-column grid of completed rituals with checkmark overlay and Reflect CTA.
 */

import { ChevronRight } from "lucide-react"
import { Carousel } from "@/components/carousel/Carousel"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import { useCompletedRituals } from "../hooks/useCompletedRituals"
import { CompletedRitualCard } from "./CompletedRitualCard"

export function LatestRitualsSection() {
  const { t } = useTranslation("myRituality")
  const user = useAuthStore(state => state.user)
  const { data, isLoading } = useCompletedRituals(user?.id || "", { limit: 2 })

  const rituals = data?.data?.rituals || []

  if (isLoading) {
    return (
      <section>
        <div className="h-6 w-48 bg-white/5 rounded animate-pulse mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {Array.from({ length: 2 }).map((_, i) => (
            <div
              key={i}
              className="h-[340px] rounded-tl-[40px] rounded-br-[40px] bg-white/5 animate-pulse"
            />
          ))}
        </div>
      </section>
    )
  }

  // Don't render section if there are no rituals
  if (rituals.length === 0) {
    return null
  }

  return (
    <section>
      {/* Header */}
      <div className="flex items-center justify-between mb-3.75 md:mb-8">
        <h2 className="text-lg md:text-2xl font-semibold leading-7.5 uppercase opacity-90 text-primary">
          {t("latestRituals.title")}
        </h2>
        <button
          type="button"
          disabled
          className="hidden md:flex items-center gap-1 text-sm font-semibold text-primary opacity-40 cursor-not-allowed"
          title={t("latestRituals.comingSoon")}
        >
          <span>{t("latestRituals.showAll")}</span>
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Carousel for mobile, Grid for desktop */}
      <div className="hidden md:block">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {rituals.map(ritual => {
            const coverImage = ritual.uploads?.find(u => u.isCover)?.filePath
            return (
              <CompletedRitualCard
                key={ritual.id}
                id={ritual.id}
                title={ritual.title}
                type={ritual.type}
                participationFee={ritual.participationFee}
                duration={ritual.duration}
                image={coverImage}
                creatorName={ritual.createdBy?.name || undefined}
                completedAt={ritual.completedAt}
              />
            )
          })}
        </div>
      </div>
      <div className="md:hidden">
        <Carousel
          data={rituals}
          renderItem={ritual => {
            const coverImage = ritual.uploads?.find(u => u.isCover)?.filePath
            return (
              <CompletedRitualCard
                id={ritual.id}
                title={ritual.title}
                type={ritual.type}
                participationFee={ritual.participationFee}
                duration={ritual.duration}
                image={coverImage}
                creatorName={ritual.createdBy?.name || undefined}
                completedAt={ritual.completedAt}
              />
            )
          }}
          itemsPerScreen={{ sm: 1, md: 1 }}
          opts={{ loop: false, dragFree: true, align: "start" }}
        />
      </div>
      <button
        type="button"
        disabled
        className="flex md:hidden items-center gap-1.25 mt-7.5 text-sm font-semibold text-primary opacity-40 cursor-not-allowed"
        title={t("latestRituals.comingSoon")}
      >
        <span>{t("latestRituals.showAll")}</span>
        <ChevronRight className="w-5 h-5" />
      </button>
    </section>
  )
}
